import { Checkbox } from '@headlessui/react';
import {
  MagnifyingGlass,
  Plus,
  SlidersHorizontal,
} from '@phosphor-icons/react';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { useNotification } from 'src/contexts/NotificationContext';
import {
  Action,
  ActionPayload,
  ActionType,
} from 'src/interfaces/action.interface';
import { ActionForm } from 'src/pages/Actions/components/ActionForm';
import { ActionTypeSelector } from 'src/pages/Actions/components/ActionTypeSelector';
import { useAgentActions } from 'src/pages/Agents/contexts/AgentActionsContext';
import { useTransientSelectedActions } from 'src/pages/Agents/contexts/TransientSelectedActionsContext';
import { LocalAction } from 'src/pages/Agents/ManageAgent';
import { actionConfig, newAction } from 'src/stubs/action.stub';

interface AgentActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const AgentActionsModal = ({
  isOpen,
  onClose,
  onOpen,
}: AgentActionsModalProps) => {
  const notification = useNotification();
  const [loading, setLoading] = useState(false);

  const [filterActions, setFilterActions] = useState('');

  const [showActionTypeSelector, setShowActionTypeSelector] = useState(false);
  const [showActionForm, setShowActionForm] = useState(false);
  const [form, setForm] = useState<ActionPayload>(newAction);

  const [selectedId, setSelectedId] = useState('');

  const [pendingActions, setPendingActions] = useState<LocalAction[]>([]);

  const {
    globalActions,
    filterActions: doFilterActions,
    setSelectedActions: setSelectedActionsContext,
    addAction,
    updateAction,
  } = useAgentActions();

  const { selectedActions, setSelectedActions } = useTransientSelectedActions();

  const submitAction = async (formData: ActionPayload) => {
    setLoading(true);

    const cloneForm = JSON.parse(JSON.stringify(formData)) as ActionPayload;
    let isJsonValid = false;

    try {
      if (cloneForm.type === ActionType.External) {
        cloneForm.label = cloneForm.config.name;
        cloneForm.description = cloneForm.config.description;
      }

      if (cloneForm.config.input_schema) {
        cloneForm.config.input_schema = JSON.parse(
          cloneForm.config.input_schema,
        );
        isJsonValid = true;
      }

      if (selectedId) {
        if (!selectedId.startsWith('local_')) {
          // Editing a server action - create a new local action
          const idLocal = `local_${Date.now()}`;
          const newAction: LocalAction = {
            ...(cloneForm as Action),
            id: idLocal,
            originalId: selectedId,
          };
          // Add to pending actions instead of global context
          setPendingActions((prev) => [...prev, newAction]);
          // Remove the original action and add the new local action to selection
          setSelectedActions([
            ...selectedActions.filter((id) => id !== selectedId),
            idLocal,
          ]);
        } else {
          // Editing an existing local/pending action - update it
          setPendingActions((prev) =>
            prev.map((action) =>
              action.id === selectedId
                ? ({ ...(cloneForm as Action), id: selectedId } as LocalAction)
                : action,
            ),
          );
          // No need to update selectedActions since the ID stays the same
        }
      } else {
        // Creating a new action
        const idLocal = `local_${Date.now()}`;
        const newAction: LocalAction = {
          ...(cloneForm as Action),
          id: idLocal,
        };
        // Add to pending actions instead of global context
        setPendingActions((prev) => [...prev, newAction]);
        setSelectedActions([...selectedActions, idLocal]);
      }

      setShowActionForm(false);
      onOpen();
      setSelectedId('');
      notification.success(
        `Action ${selectedId ? 'updated' : 'created'} successfully`,
      );
    } catch {
      if (!isJsonValid) {
        notification.error('Invalid JSON in input schema');
      } else {
        notification.error('Failed to save action');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateActions = () => {
    // Commit all pending actions to global context
    pendingActions.forEach((action) => {
      if (action.originalId) {
        // It's an edited server action
        updateAction(action);
      } else {
        // It's a new action
        addAction(action);
      }
    });

    // Update selected actions in global context
    setSelectedActionsContext(selectedActions);

    // Clear pending state and close
    setPendingActions([]);
    onClose();
  };

  const handleModalClose = () => {
    // Clear pending actions when closing without saving
    setPendingActions([]);
    onClose();
  };

  const handleModalHide = () => {
    onClose();
  };

  // Always show pending actions (newly created/edited actions should always be visible)
  // Only apply search filter to global actions
  const filteredGlobalActions =
    filterActions.length > 0
      ? globalActions.filter((action) =>
          action.label?.toLowerCase().includes(filterActions.toLowerCase()),
        )
      : globalActions;

  // Merge pending actions with filtered global actions for display
  const displayActions = [
    ...pendingActions, // Always show all pending actions
    ...filteredGlobalActions.filter(
      (globalAction) =>
        !pendingActions.some(
          (pendingAction) =>
            pendingAction.id === globalAction.id ||
            pendingAction.originalId === globalAction.id,
        ),
    ),
  ];

  // Clean up selectedActions to only include actions that are actually visible
  const visibleActionIds = displayActions.map((action) => action.id);
  const cleanedSelectedActions = selectedActions.filter((id) =>
    visibleActionIds.includes(id),
  );

  // Fetch actions when modal opens
  useEffect(() => {
    if (isOpen) {
      doFilterActions(''); // Fetch all actions initially
    }
  }, [isOpen, doFilterActions]);

  return (
    <>
      <Modal
        title="Select Actions to Add"
        isOpen={isOpen}
        onClose={handleModalClose}
        actionButton={
          <Button onClick={updateActions}>
            Add ({cleanedSelectedActions?.length || 0}) action
            {cleanedSelectedActions?.length !== 1 ? 's' : ''}
          </Button>
        }
      >
        <div className="flex flex-col w-full">
          <Input
            type="text"
            required
            placeholder="Search actions"
            value={filterActions}
            onChange={(e) => setFilterActions(e.target.value)}
            icon={<MagnifyingGlass size={20} />}
            iconPosition="left"
            className="mb-4 block w-full"
            disabled={loading}
          />
        </div>

        <div className="flex flex-col w-full gap-2">
          {displayActions
            .filter((action) => action.id)
            .map((item) => (
              <div
                key={item.id}
                className={clsx(
                  'flex flex-row w-full items-center hover:bg-neutral-50 cursor-pointer p-4 rounded-md border',
                  cleanedSelectedActions?.includes(item.id)
                    ? 'border-2 border-primary'
                    : 'border-2 border-neutral-0 hover:border-neutral-50',
                )}
                onClick={() => {
                  const actions = selectedActions || [];
                  setSelectedActions(
                    actions.includes(item.id)
                      ? actions.filter((id) => id !== item.id)
                      : [...actions, item.id],
                  );
                }}
              >
                <div className="flex flex-row w-full items-center">
                  <Checkbox
                    disabled={loading}
                    checked={cleanedSelectedActions?.includes(item.id)}
                    key={item.id}
                    onChange={(checked: boolean) => {
                      if (checked) {
                        setSelectedActions([...selectedActions, item.id]);
                      } else {
                        setSelectedActions(
                          selectedActions.filter((id) => id !== item.id),
                        );
                      }
                    }}
                    className={`
                ${cleanedSelectedActions?.includes(item.id) ? 'bg-primary border-transparent' : 'bg-neutral-0 border-neutral-200'}
                relative inline-flex h-5 w-5 items-center justify-center rounded border-2 mr-3
                transition-colors duration-200 ease-in-out
                focus:outline-none
              `}
                  >
                    <span
                      className={`
                    pointer-events-none absolute h-3 w-3 transform
                    ${cleanedSelectedActions?.includes(item.id) ? 'opacity-100' : 'opacity-0'}
                      transition-opacity duration-200 ease-in-out
                    `}
                    >
                      <svg
                        className="h-3 w-3 text-neutral-0"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M3 6l2 2 4-4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </Checkbox>
                  <div className="w-[50px] h-[50px] bg-neutral-200/25 rounded-lg flex items-center justify-center">
                    <img
                      src={`/assets/actions/modal/${actionConfig[item.type].icon}`}
                      alt={actionConfig[item.type].label}
                      className={clsx(
                        'flex select-none pointer-events-none',
                        actionConfig[item.type].iconStyling,
                      )}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="text-medium strong text-neutral-600">
                      {item.label || 'Untitled'}
                    </div>
                    <div className="text-description text-neutral-500 leading-6">
                      {actionConfig[item.type].label}
                    </div>
                  </div>
                </div>

                <div className="flex flex-row items-center gap-2">
                  <button
                    className="p-2 hover:bg-transparent focus:bg-transparent active:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      setForm({
                        ...item,
                        type: item.type,
                        ...(item.config.input_schema && {
                          config: {
                            ...item.config,
                            input_schema: JSON.stringify(
                              item.config.input_schema,
                            ),
                          },
                        }),
                      });
                      setSelectedId(item.id);
                      setShowActionForm(true);
                      handleModalHide();
                    }}
                  >
                    <SlidersHorizontal size={20} className="rotate-90" />
                  </button>
                </div>
              </div>
            ))}
        </div>

        <div className="flex flex-row w-full justify-start text-primary font-normal mt-6">
          <button
            onClick={() => {
              setShowActionTypeSelector(true);
              handleModalHide();
            }}
            className="flex flex-row items-center gap-2 hover:bg-transparent focus:bg-transparent active:bg-transparent"
          >
            <Plus size={20} />
            <a>Add New Action</a>
          </button>
        </div>
      </Modal>

      <ActionTypeSelector
        isOpen={showActionTypeSelector}
        onClose={() => {
          setShowActionTypeSelector(false);
          onOpen();
        }}
        onSelect={(type) => {
          setForm({ ...newAction, type });
        }}
        onContinue={() => {
          setShowActionTypeSelector(false);
          setShowActionForm(true);
        }}
        selectedType={form.type}
      />

      <ActionForm
        key={form.type + '_' + selectedId}
        isOpen={showActionForm}
        onClose={() => {
          setShowActionForm(false);
          onOpen();
        }}
        onSubmit={submitAction}
        initialForm={form}
        selectedId={selectedId}
        loading={loading}
        needsPhoneNumber={false}
        showRequiredFields={false}
      >
        {selectedId !== '' && (
          <div className="inline-block gap-2.5 p-2.5 text-sm bg-primary-100 rounded-md mb-6">
            Editing this setting will create it as a new setting
          </div>
        )}

        {selectedId === '' && (
          <div className="inline-block gap-2.5 p-2.5 text-sm bg-primary-100 rounded-md mb-6">
            Fill in the action details to create a new action
          </div>
        )}
      </ActionForm>
    </>
  );
};
